import axios from 'api/axios-configure'
import { Checkbox, Grid } from '@mui/material'
import { AlertBanner, AlertBannerText, Button, Dropdown, RadioButton, TextField } from 'monday-ui-react-core'
import React, { useContext, useEffect, useState } from 'react'
import { ChevronLeft } from 'react-bootstrap-icons'
import { Link, useNavigate } from 'react-router-dom'
import DatePicker from 'rsuite/DatePicker'
import { isValidEmail } from 'utils/commonUtils'
import AccountCreationSuccess from './AccountCreationSuccess'
import './CreateAccountWizard.scss'

const STEP_TYPE = {
    FIRST: 'FIRST',
    SECOND: 'SECOND',
}

const ORGANIZATION_TYPE = {
    INDIVIDUAL: 'INDIVIDUAL',
    COMPANY: 'COMPANY',
}


const VERIFICATION_METHOD_OPTIONS_IDS = {
    PAYOUT_STATEMENT: 'payout-statement',
    PRODUCT_BALANCING_STATEMENT: 'product-balancing-statement',
    REGISTRATION_CODE: 'registration-code'
}

const VERIFICATION_METHOD_OPTIONS = [
    {
        value: VERIFICATION_METHOD_OPTIONS_IDS.PAYOUT_STATEMENT,
        label: 'Payout Statement',
    },
    {
        value: VERIFICATION_METHOD_OPTIONS_IDS.PRODUCT_BALANCING_STATEMENT,
        label: 'Gas Balancing Statement',
    },
    {
        value: VERIFICATION_METHOD_OPTIONS_IDS.REGISTRATION_CODE,
        label: 'Registration Code',
    },
]

const STRING_ONLY = /^[a-zA-Z_ \-]+$/
const ALPHANUMERIC_ONLY = /^[a-zA-Z0-9_ \-]+$/
const NUMERIC_ONLY = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/

const CreateAccountWizard = () => {
    const navigate = useNavigate()
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [organizationName, setOrganizationName] = useState('')
    const [sender, setSender] = useState('')
    const [verificationMethod, setVerificationMethod] = useState(null)
    const [agree, setAgree] = useState(false)
    const [errorMessage, setErrorMessage] = useState(undefined)
    const [selectedOrganization, setSelectedOrganization] = useState()
    const [selectedEmailNotification, setSelectedEmailNotification] = useState()
    const [displayOrganizationSection, setDisplayOrganizationSection] =
        useState(false)
    const [step, setStep] = useState(STEP_TYPE.FIRST)

    const [payoutId, setPayoutId] = useState('')
    const [reportingMonth, setReportingMonth] = useState('')
    const [reportingCenter, setReportingCenter] = useState('')
    const [statementDate, setStatementDate] = useState('')
    const [ownerNumber, setOwnerNumber] = useState('')
    const [payoutBalance, setPayoutBalance] = useState('')
    const [comulativeImbalance, setComulativeImbalance] = useState('')
    const [registrationCode, setRegistrationCode] = useState('')
    const [openModal, setOpenModal] = useState(false)
    const [displayStepOneErrorBanner, setDisplayStepOneErrorBanner] = useState(false)
    const [displayStepTwoErrorBanner, setDisplayStepTwoErrorBanner] = useState(false)
    const [stepTwoErrorMessage, setStepTwoErrorMessage] = useState('')

    const handleCloseModal = () => {
        setOpenModal(false)
        navigate('/signin')
    }

    const clearVerificationMethodFields = () => {
        setPayoutId('')
        setReportingMonth('')
        setReportingCenter('')
        setStatementDate('')
        setOwnerNumber('')
        setPayoutBalance('')
        setComulativeImbalance('')
        setRegistrationCode('')
    }

    const handleOrganizationChange = (event) => {
        const type = event.target.value
        setDisplayOrganizationSection(type === ORGANIZATION_TYPE.COMPANY)
        setSelectedOrganization(event.target.value)
    }

    const handleEmailNotificationChange = (event) => {
        setSelectedEmailNotification(event.target.value)
    }

    const handleNextButton = () => {
        setDisplayStepOneErrorBanner(false)
        setDisplayStepTwoErrorBanner(false)
        axios
            .get(
                '/ValidateUserEmail', 
                {
                    params: {
                        email
                    },
                })
            .then((response) => {
                if (response.status === 200) {
                    setStep(STEP_TYPE.SECOND)
                }
            })
            .catch((err) => {
                if (err.response.status === 409) {
                    setDisplayStepOneErrorBanner(true)
                }
            })
    }
    const handleCancelButton = () => {
        navigate('/signin')
    }

    const validateFirstStep = () => {
        if (
            !validateStringOnly(firstName) &&
            !validateStringOnly(lastName) &&
            email != '' &&
            isValidEmail(email) &&
            selectedOrganization &&
            selectedEmailNotification !== undefined &&
            !validateAlphaNumeric(organizationName)
        ) {
            return true
        }

        return false
    }

    const validateCreateAccount = () => {
        if (!verificationMethod) return false;

        const isVerificationPayoutStatementValid = 
                verificationMethod.value == VERIFICATION_METHOD_OPTIONS_IDS.PAYOUT_STATEMENT 
            &&  !validateAlphaNumeric(payoutId)
            &&  reportingMonth
            &&  statementDate
            &&  !validateAlphaNumeric(ownerNumber)
            &&  (payoutBalance !== '' && !validateNumberOnly(payoutBalance));
        
        const isVerificationProductBalancingStatementValid = 
                verificationMethod.value == VERIFICATION_METHOD_OPTIONS_IDS.PRODUCT_BALANCING_STATEMENT 
            &&  !validateAlphaNumeric(reportingCenter)
            &&  reportingMonth
            &&  statementDate
            &&  !validateAlphaNumeric(ownerNumber)
            &&  (payoutBalance !== '' && !validateNumberOnly(payoutBalance));  
            
        const isVerificationRegistrationCodeValid = 
                verificationMethod.value == VERIFICATION_METHOD_OPTIONS_IDS.REGISTRATION_CODE 
            &&  !validateAlphaNumeric(ownerNumber)
            &&  !validateAlphaNumeric(registrationCode);  

        if (!validateAlphaNumeric(sender) &&
            verificationMethod &&
            (isVerificationPayoutStatementValid || isVerificationProductBalancingStatementValid || isVerificationRegistrationCodeValid) &&
            agree
        ) {
            return true;
        }
        return false;
    }

    const handleCreateAccount = () => {
        const account = {
            firstName,
            lastName,
            email,
            orgType: selectedOrganization == ORGANIZATION_TYPE.COMPANY ? 1 : 2,
            orgName: organizationName,
            senderNumber: sender,
            verificationMethod: verificationMethod.value == VERIFICATION_METHOD_OPTIONS_IDS.PAYOUT_STATEMENT ? 'PO' : 
                                verificationMethod.value == VERIFICATION_METHOD_OPTIONS_IDS.PRODUCT_BALANCING_STATEMENT ? 'PB' :
                                verificationMethod.value == VERIFICATION_METHOD_OPTIONS_IDS.REGISTRATION_CODE ? 'CD' : '',
            reportingMonth: reportingMonth ? new Date(reportingMonth).toISOString().split('T')[0] : null, //2024-04-28
            statementDate: statementDate ? new Date(statementDate).toISOString().split('T')[0] : null, //2024-04-28
            reportingCenter: verificationMethod.value == VERIFICATION_METHOD_OPTIONS_IDS.PAYOUT_STATEMENT ? payoutId :
                             verificationMethod.value == VERIFICATION_METHOD_OPTIONS_IDS.PRODUCT_BALANCING_STATEMENT ? reportingCenter : null,
            ownerNumber,
            balance: payoutBalance ? payoutBalance : null,
            phone,
            registrationCode,
            emailNotification: selectedEmailNotification === 'true'
        }

        setDisplayStepTwoErrorBanner(false)
        axios
            .post('/CreateNewUserAccount', account)
            .then((response) => {
                if (response.status === 200) {
                    setOpenModal(true)
                }
            })
            .catch((err) => {
                if (err.response.data.statusCode === 400) {
                    setStepTwoErrorMessage(err.response.data.message)
                } else {
                    setStepTwoErrorMessage('An error ocurred while processing the request')
                }
                setDisplayStepTwoErrorBanner(true)
            })
    }

    const validateStringOnly = (field) => !STRING_ONLY.test(field)
    const validateNumberOnly = (field) => isNaN(+field.replace(/,/g, ''))
    const validatePhoneNumber = (field) => !NUMERIC_ONLY.test(field)    
    const validateAlphaNumeric = (field) => !ALPHANUMERIC_ONLY.test(field)

    const handleBackButton = () => {
        setStep(STEP_TYPE.FIRST)
    }

    useEffect(() => {
        if (!displayOrganizationSection) setOrganizationName('')
    }, [displayOrganizationSection])

    useEffect(() => {
        clearVerificationMethodFields()
    }, verificationMethod)

    return (
        <>
            <div className="mt-4 mx-2 create-account-form">
                {step === STEP_TYPE.FIRST && (
                    <section id="first-section">
                        <h1>Create Account - Step 1 of 2</h1>
                        {displayStepOneErrorBanner && (
                            <AlertBanner backgroundColor='negative' isCloseHidden>
                                <AlertBannerText text="Errors found in the form: Email already in use" />
                            </AlertBanner>
                        )}
                        <h3 className='mt-1'>User Account</h3>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={8} md={6} lg={3}>
                                <TextField
                                    id="first-name"
                                    value={firstName}
                                    onChange={(event) => setFirstName(event)}
                                    title={(<>First Name<b style={{color:'red'}}>*</b></>)}
                                    placeholder="Enter your first name"
                                    size={TextField.sizes.SMALL}
                                    validation={{
                                        status:
                                            firstName == ''
                                                ? ''
                                                : validateStringOnly(firstName)
                                                ? 'error'
                                                : 'success',
                                        text:
                                            firstName == ''
                                                ? ''
                                                : validateStringOnly(firstName)
                                                ? 'Please enter a valid First Name'
                                                : '',
                                    }}
                                    maxLength={128} //TBD: DB
                                />
                            </Grid>
                            <Grid item xs={12} sm={8} md={6} lg={9}></Grid>
                            <Grid item xs={12} sm={8} md={6} lg={3}>
                                <TextField
                                    id="last-name"
                                    value={lastName}
                                    onChange={(event) => setLastName(event)}
                                    title={(<>Last Name<b style={{color:'red'}}>*</b></>)}
                                    placeholder="Enter your last name"
                                    size={TextField.sizes.SMALL}
                                    validation={{
                                        status:
                                            lastName == ''
                                                ? ''
                                                : validateStringOnly(lastName)
                                                ? 'error'
                                                : 'success',
                                        text:
                                            lastName == ''
                                                ? ''
                                                : validateStringOnly(lastName)
                                                ? 'Please enter a valid Last Name'
                                                : '',
                                    }}
                                    maxLength={128} //TBD: DB
                                    requiredAsterisk
                                />
                            </Grid>
                            <Grid item xs={12} sm={8} md={6} lg={9}></Grid>
                            <Grid item xs={12} sm={8} md={6} lg={3}>
                                <TextField
                                    id="email"
                                    value={email}
                                    onChange={(event) => setEmail(event)}
                                    title={(<>Email<b style={{color:'red'}}>*</b></>)}
                                    placeholder="Enter your email"
                                    size={TextField.sizes.SMALL}
                                    validation={{
                                        status:
                                            email == ''
                                                ? ''
                                                : !isValidEmail(email)
                                                ? 'error'
                                                : 'success',
                                        text:
                                            email == ''
                                                ? ''
                                                : !isValidEmail(email) &&
                                                  email.length
                                                ? 'Please enter a valid email address.'
                                                : '',
                                    }}
                                    maxLength={128}
                                    requiredAsterisk
                                />
                            </Grid>
                            <Grid item xs={12} sm={8} md={6} lg={9}></Grid>
                            <Grid item xs={12} sm={8} md={6} lg={3}>
                                <TextField
                                    id="phone"
                                    value={phone}
                                    onChange={(event) => setPhone(event)}
                                    title="Phone"
                                    placeholder="Enter your phone number"
                                    size={TextField.sizes.SMALL}
                                    validation={{
                                        status:
                                            phone == ''
                                                ? ''
                                                : validatePhoneNumber(phone)
                                                ? 'error'
                                                : 'success',
                                        text:
                                            phone == ''
                                                ? ''
                                                : validatePhoneNumber(phone)
                                                ? 'Please enter a valid phone number.'
                                                : '',
                                    }}
                                    maxLength={128}
                                />
                            </Grid>
                        </Grid>
                        
                        <h3 className='mt-3'>Organization</h3>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <p>
                                    I am creating this account to access
                                    statements and data on behalf of a:
                                </p>
                            </Grid>
                            <Grid item xs={12}>
                                <RadioButton
                                    onSelect={handleOrganizationChange}
                                    checked={
                                        selectedOrganization ===
                                        ORGANIZATION_TYPE.INDIVIDUAL
                                    }
                                    value={ORGANIZATION_TYPE.INDIVIDUAL}
                                    text="Individual/Myself"
                                    name="organization-radio-buttons"
                                />
                                <RadioButton
                                    onSelect={handleOrganizationChange}
                                    checked={
                                        selectedOrganization ===
                                        ORGANIZATION_TYPE.COMPANY
                                    }
                                    value={ORGANIZATION_TYPE.COMPANY}
                                    text="Company/Business"
                                    name="organization-radio-buttons"
                                />
                            </Grid>
                            {selectedOrganization && (
                                <>
                                    <Grid item xs={12} sm={8} md={6} lg={3}>
                                        <TextField
                                            id="organization-name"
                                            value={organizationName}
                                            onChange={(event) =>
                                                setOrganizationName(event)
                                            }
                                            title={(<>Organization Name<b style={{color:'red'}}>*</b></>)}
                                            placeholder="Enter your organization name"
                                            size={TextField.sizes.SMALL}
                                            validation={{
                                                status:
                                                    organizationName == ''
                                                        ? ''
                                                        : validateAlphaNumeric(
                                                              organizationName
                                                          )
                                                        ? 'error'
                                                        : 'success',
                                                text:
                                                    organizationName == ''
                                                        ? ''
                                                        : validateAlphaNumeric(
                                                              organizationName
                                                          )
                                                        ? 'Please enter a valid organization name'
                                                        : '',
                                            }}
                                            maxLength={128} //TBD: DB
                                        />
                                    </Grid>
                                    <Grid item xs={0} sm={4} md={6} lg={9}></Grid>
                                </>
                            )}
                        </Grid>

                        <h3 className='mt-3'>Email Notifications</h3>
                        <Grid container spacing={2} className="">
                            <Grid item xs={12} sm={8} md={6} lg={6}>
                                <p>
                                    EnergilityDX will send email notifications when new statements or information are available
                                    for you to view. 
                                    Your email address will only be used for these notifications in accordance with our <a href='https://energilitydx.com/privacy/'>Privacy Policy</a>. 
                                    You can change this setting any time in your user profile.
                                </p>
                            </Grid>

                            <Grid item xs={12}>
                                <RadioButton
                                    onSelect={handleEmailNotificationChange}
                                    checked={
                                        selectedEmailNotification ===
                                        'true'
                                    }
                                    value={true}
                                    text="Yes, please send me email notifications."
                                    name="email-notifications-radio-buttons"
                                />
                                <RadioButton
                                    onSelect={handleEmailNotificationChange}
                                    checked={
                                        selectedEmailNotification ===
                                        'false'
                                    }
                                    value={false}
                                    text="No, thanks. I do not want to receive email notifications."
                                    name="email-notifications-radio-buttons"
                                />
                            </Grid>
                        </Grid>
                    </section>
                )}
                {step == STEP_TYPE.SECOND && (
                    <section id="second-section">
                        <Link
                            className="d-flex  align-items-center"
                            onClick={handleBackButton}
                        >
                            <ChevronLeft size={13} />
                            <span>Back</span>
                        </Link>
                        <h1>Create Account - Step 2 of 2</h1>
                        {displayStepTwoErrorBanner && (
                            <AlertBanner backgroundColor='negative' isCloseHidden>
                                <AlertBannerText text={stepTwoErrorMessage} />
                            </AlertBanner>
                        )}
                        <h3>Account Verification</h3>
                        <Grid container spacing={2} className="">
                            <Grid item xs={12}>
                                <p>
                                    Please select an account verification method
                                    to allow us to verify your identity:
                                </p>
                            </Grid>
                            <Grid item xs={12} sm={8} md={6} lg={3}>
                                <TextField
                                    id="sender"
                                    value={sender}
                                    onChange={(event) =>
                                        setSender(event)
                                    }
                                    title={(<>Sender<b style={{color:'red'}}>*</b></>)}
                                    placeholder="Enter your Sender ID"
                                    size={TextField.sizes.SMALL}
                                    maxLength={128}
                                    validation={{
                                        status:
                                        sender == ''
                                                ? ''
                                                : validateAlphaNumeric(
                                                    sender
                                                    )
                                                ? 'error'
                                                : 'success',
                                        text:
                                        sender == ''
                                                ? ''
                                                : validateAlphaNumeric(
                                                    sender
                                                    )
                                                ? 'Please enter a valid payout id'
                                                : '',
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={8} md={6} lg={9}></Grid>
                            <Grid item xs={12} sm={8} md={6} lg={3}>
                                <p>
                                    Select an account verification method<b style={{color:'red'}}>*</b>
                                </p>
                                <Dropdown
                                    size={Dropdown.size.SMALL}
                                    options={VERIFICATION_METHOD_OPTIONS}
                                    onChange={setVerificationMethod}
                                    clearable={false}
                                />
                            </Grid>
                            <Grid item xs={12} sm={8} md={6} lg={9}></Grid>
                            {verificationMethod?.value ===
                                VERIFICATION_METHOD_OPTIONS[0].value && (
                                <>
                                    <Grid item xs={12} sm={8} md={6} lg={3}>
                                        <TextField
                                            id="payout-id"
                                            value={payoutId}
                                            onChange={(event) =>
                                                setPayoutId(event)
                                            }
                                            title={(<>Payout ID<b style={{color:'red'}}>*</b></>)}
                                            placeholder="Enter your Payout ID"
                                            size={TextField.sizes.SMALL}
                                            maxLength={128}
                                            validation={{
                                                status:
                                                    payoutId == ''
                                                        ? ''
                                                        : validateAlphaNumeric(
                                                              payoutId
                                                          )
                                                        ? 'error'
                                                        : 'success',
                                                text:
                                                    payoutId == ''
                                                        ? ''
                                                        : validateAlphaNumeric(
                                                              payoutId
                                                          )
                                                        ? 'Please enter a valid payout id'
                                                        : '',
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={9}></Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={3}>
                                        <p>
                                            Reporting Month<b style={{color:'red'}}>*</b>
                                        </p>
                                        <DatePicker className='date-picker'
                                            placeholder="Reporting Month"
                                            format="MM/dd/yyyy"
                                            onChange={setReportingMonth}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={9}></Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={3}>
                                        <p>
                                            Select a Statement Date<b style={{color:'red'}}>*</b>
                                        </p>
                                        <DatePicker className='date-picker'
                                            placeholder="Statement Date"
                                            format="MM/dd/yyyy"
                                            onChange={setStatementDate}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={9}></Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={3}>
                                        <TextField
                                            id="owner-number"
                                            value={ownerNumber}
                                            onChange={(event) =>
                                                setOwnerNumber(event)
                                            }
                                            title={(<>Owner Number<b style={{color:'red'}}>*</b></>)}
                                            placeholder="Enter your Owner Number"
                                            size={TextField.sizes.SMALL}
                                            maxLength={128}
                                            validation={{
                                                status:
                                                    ownerNumber == ''
                                                        ? ''
                                                        : validateAlphaNumeric(
                                                              ownerNumber
                                                          )
                                                        ? 'error'
                                                        : 'success',
                                                text:
                                                    ownerNumber == ''
                                                        ? ''
                                                        : validateAlphaNumeric(
                                                              ownerNumber
                                                          )
                                                        ? 'Please enter a valid owner number'
                                                        : '',
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={9}></Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={3}>
                                        <TextField
                                            id="payout-balance"
                                            value={payoutBalance}
                                            onChange={(event) =>
                                                setPayoutBalance(event)
                                            }
                                            title={(<>Payout Balance<b style={{color:'red'}}>*</b></>)}
                                            placeholder="Enter your Payout Balance"
                                            size={TextField.sizes.SMALL}
                                            maxLength={128}
                                            validation={{
                                                status:
                                                    payoutBalance == ''
                                                        ? ''
                                                        : validateNumberOnly(
                                                              payoutBalance
                                                          )
                                                        ? 'error'
                                                        : 'success',
                                                text:
                                                    payoutBalance == ''
                                                        ? ''
                                                        : validateNumberOnly(
                                                              payoutBalance
                                                          )
                                                        ? 'Please enter a valid balance amount'
                                                        : '',
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} lg={9}></Grid>
                                </>
                            )}
                            {verificationMethod?.value ===
                                VERIFICATION_METHOD_OPTIONS[1].value && (
                                <>
                                    <Grid item xs={12} sm={8} md={6} lg={3}>
                                        <TextField
                                            id="reporting-center"
                                            value={reportingCenter}
                                            onChange={(event) =>
                                                setReportingCenter(event)
                                            }
                                            title={(<>Reporting Center<b style={{color:'red'}}>*</b></>)}
                                            placeholder="Enter your Reporting Center"
                                            size={TextField.sizes.SMALL}
                                            maxLength={128}
                                            validation={{
                                                status:
                                                    reportingCenter == ''
                                                        ? ''
                                                        : validateAlphaNumeric(
                                                              reportingCenter
                                                          )
                                                        ? 'error'
                                                        : 'success',
                                                text:
                                                    reportingCenter == ''
                                                        ? ''
                                                        : validateAlphaNumeric(
                                                              reportingCenter
                                                          )
                                                        ? 'Please enter a valid reporting center'
                                                        : '',
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={9}></Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={3}>
                                        <p>
                                            Reporting Month<b style={{color:'red'}}>*</b>
                                        </p>
                                        <DatePicker className='date-picker'
                                            placeholder="Reporting Month"
                                            format="MM/dd/yyyy"
                                            onChange={setReportingMonth}
                                        />                                     
                                    </Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={9}></Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={3}>
                                        <p>
                                            Select a Statement Date<b style={{color:'red'}}>*</b>
                                        </p>
                                        <DatePicker
                                            placeholder="Statement Date"
                                            format="MM/dd/yyyy"
                                            onChange={setStatementDate}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={9}></Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={3}>
                                        <TextField
                                            id="owner-number"
                                            value={ownerNumber}
                                            onChange={(event) =>
                                                setOwnerNumber(event)
                                            }
                                            title={(<>Owner Number<b style={{color:'red'}}>*</b></>)}
                                            placeholder="Enter your Owner Number"
                                            size={TextField.sizes.SMALL}
                                            maxLength={128}
                                            validation={{
                                                status:
                                                    ownerNumber == ''
                                                        ? ''
                                                        : validateAlphaNumeric(
                                                              ownerNumber
                                                          )
                                                        ? 'error'
                                                        : 'success',
                                                text:
                                                    ownerNumber == ''
                                                        ? ''
                                                        : validateAlphaNumeric(
                                                              ownerNumber
                                                          )
                                                        ? 'Please enter a valid owner number'
                                                        : '',
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={9}></Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={3}>
                                        <TextField
                                            id="cumulative-imbalance"
                                            value={payoutBalance}
                                            onChange={(event) =>
                                                setPayoutBalance(event)
                                            }
                                            title={(<>Cumulative Imbalance<b style={{color:'red'}}>*</b></>)}
                                            placeholder="Enter your Cumulative Imbalance"
                                            size={TextField.sizes.SMALL}
                                            maxLength={128}
                                            validation={{
                                                status:
                                                    payoutBalance == ''
                                                        ? ''
                                                        : validateNumberOnly(
                                                              payoutBalance
                                                          )
                                                        ? 'error'
                                                        : 'success',
                                                text:
                                                    payoutBalance == ''
                                                        ? ''
                                                        : validateNumberOnly(
                                                              payoutBalance
                                                          )
                                                        ? 'Please enter a valid balance amount'
                                                        : '',
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} lg={9}></Grid>
                                </>
                            )}
                            {verificationMethod?.value ===
                                VERIFICATION_METHOD_OPTIONS[2].value && (
                                <>
                                    <Grid item xs={12} sm={8} md={6} lg={3}>
                                        <TextField
                                            id="owner-number"
                                            value={ownerNumber}
                                            onChange={(event) =>
                                                setOwnerNumber(event)
                                            }
                                            title={(<>Owner Number<b style={{color:'red'}}>*</b></>)}
                                            placeholder="Enter your Owner Number"
                                            size={TextField.sizes.SMALL}
                                            maxLength={128}
                                            validation={{
                                                status:
                                                    ownerNumber == ''
                                                        ? ''
                                                        : validateAlphaNumeric(
                                                              ownerNumber
                                                          )
                                                        ? 'error'
                                                        : 'success',
                                                text:
                                                    ownerNumber == ''
                                                        ? ''
                                                        : validateAlphaNumeric(
                                                              ownerNumber
                                                          )
                                                        ? 'Please enter a valid owner number'
                                                        : '',
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={9}></Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={3}>
                                        <TextField
                                            id="registration-code"
                                            value={registrationCode}
                                            onChange={(event) =>
                                                setRegistrationCode(event)
                                            }
                                            title={(<>Registration Code<b style={{color:'red'}}>*</b></>)}
                                            placeholder="Enter your Registration Code"
                                            size={TextField.sizes.SMALL}
                                            maxLength={128}
                                            validation={{
                                                status:
                                                    registrationCode == ''
                                                        ? ''
                                                        : validateAlphaNumeric(
                                                              registrationCode
                                                          )
                                                        ? 'error'
                                                        : 'success',
                                                text:
                                                    registrationCode == ''
                                                        ? ''
                                                        : validateAlphaNumeric(
                                                              registrationCode
                                                          )
                                                        ? 'Please enter a valid registration code'
                                                        : '',
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} lg={9}></Grid>
                                </>
                            )}
                            
                            
                            <Grid item>
                                <Checkbox
                                    checked={agree}
                                    onChange={(event) =>
                                        setAgree(event.target.checked)
                                    }
                                    inputProps={{ 'aria-label': 'controlled' }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={8} md={6} lg={5}>
                                <p style={{paddingTop: '9px'}}>
                                    By clicking Create account, I agree to the
                                    EnergilityDX <a href="https://energilitydx.com/privacy/">Privacy Policy</a> and <a href="https://energilitydx.com/terms/">Terms of 
                                    Service</a>
                                </p>
                            </Grid>
                        </Grid>
                    </section>
                )}
                <Grid
                    container
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    className='mt-3 mb-3'
                    xs={12} sm={12} md={9} lg={6}
                >
                    <Grid item mr={2}>
                        <Button
                            size={Button.sizes.SMALL}
                            type={Button.types.BUTTON}
                            onClick={handleCancelButton}
                            className="cancel"
                        >
                            Cancel
                        </Button>
                    </Grid>
                    {step === STEP_TYPE.FIRST && (
                        <Grid item>
                            <Button
                                size={Button.sizes.SMALL}
                                type={Button.types.BUTTON}
                                onClick={handleNextButton}
                                disabled={!validateFirstStep()}
                            >
                                Next
                            </Button>
                        </Grid>
                    )}
                    {step === STEP_TYPE.SECOND && (
                        <Grid item>
                            <Button
                                size={Button.sizes.SMALL}
                                type={Button.types.BUTTON}
                                className="create-account"
                                disabled={!validateCreateAccount()}
                                onClick={handleCreateAccount}
                            >
                                Create Account
                            </Button>
                        </Grid>
                    )}
                </Grid>
            </div>

            {openModal && (
                <AccountCreationSuccess open={openModal} onClose={handleCloseModal} email={email} organization={organizationName}></AccountCreationSuccess>
            )}
        </>
    )
}

export default CreateAccountWizard
